import {ListPresentation} from 'aplovio/list-ui';

(() => {
  // A bounded page navigator. Shared shell and layer owners stay mounted;
  // pages with custom scripts/app blocks retain native document navigation.
  if (!window.AplovioCarePages && !window.Shopify?.designMode && document.querySelector('main .ap-care')) {
    class CarePages {
      constructor() {
        this.main = document.querySelector('main');
        this.url = location.href;
        this.presentation = new ListPresentation(this.main, {navigation:'.shopify-section:has(> [data-care-navigation])'});
        this.animations = this.presentation.motion.animations;
        this.prefetched = new Map();
        this.motion = this.presentation.motion.reducedMotion;
        for (const type of ['pointerover', 'focusin']) this.main.addEventListener(type, event => {
          const link = event.target.closest('[data-care-navigation] a');
          const url = link && this.destination(link.href);
          if (url && !this.samePage(url.href, this.url) && this.supported(document)) this.prefetch(url);
        });
        document.addEventListener('click', event => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
          const link = event.target.closest('a[href]');
          if (!link || !this.main.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
          if (!link.closest('[data-care-navigation],.ap-care-faq-head,.ap-care-answer') || !this.supported(document)) return;
          const url = this.destination(link.href);
          if (!url || window.AplovioLayers?.top()) return;
          event.preventDefault();
          this.load(url);
        });
        window.addEventListener('popstate', event => {
          if (this.samePage(location.href, this.url)) { this.request?.abort(); this.request = null; this.busy(false); return; } // Shared drawers own same-page history.
          if (!event.state?.aplovioCare || !this.supported(document)) { location.reload(); return; }
          this.load(new URL(location.href), {history: true, scroll: event.state.aplovioCare.scroll});
        });
        window.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.remember(), 150);
        }, {passive: true});
        window.addEventListener('pagehide', () => { this.request?.abort(); this.request = null; this.busy(false); this.cancelMotion(); });
        this.remember();
        this.reveal();
      }
      samePage(a, b) { const x = new URL(a), y = new URL(b); return x.origin === y.origin && x.pathname === y.pathname && x.search === y.search; }
      destination(href) {
        const url = new URL(href, location.href);
        if (url.origin !== location.origin || !/\/pages\/[^/]+\/?$/.test(url.pathname) || url.hash) return null;
        return [...this.main.querySelectorAll('[data-care-navigation] a')].some(link => this.samePage(link.href, url.href)) ? url : null;
      }
      supported(doc) {
        const main = doc.querySelector('main');
        if (!main?.querySelector('[data-care-ajax="true"]') || main.querySelector('.shopify-app-block')) return false;
        if (![...main.children].every(node => node.classList.contains('shopify-section') && node.querySelector('.ap-care'))) return false;
        return [...main.querySelectorAll('script')].every(node => node.type === 'application/json' || node.type === 'application/ld+json' || (node.src && new URL(node.getAttribute('src'), location.href).pathname.endsWith('/aplovio-care.js')));
      }
      remember() {
        if (this.request || !this.samePage(location.href, this.url) || window.AplovioLayers?.top()) return;
        history.replaceState({...history.state, aplovioCare: {url: this.url, scroll: scrollY}}, '', location.href);
      }
      busy(value, url) {
        this.main.toggleAttribute('data-care-loading', value);
        this.main.toggleAttribute('data-list-loading', value);
        this.main.setAttribute('aria-busy', String(value));
        for (const node of this.main.querySelectorAll('.ap-care-cards,.ap-care-faq-card')) node.inert = value;
        for (const link of this.main.querySelectorAll('[data-care-navigation] a')) link.toggleAttribute('data-care-pending', value && this.samePage(link.href, url.href));
        const progress = this.main.querySelector('[data-care-progress]');
        if (progress) progress.hidden = !value;
        this.positionLoader();
      }
      positionLoader() { this.presentation.update(); }
      async fetchPage(url, signal) {
        const response = await window.AplovioLazy.read(url.href, {signal, credentials: 'same-origin', headers: {Accept: 'text/html'}});
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html') || !this.samePage(response.url, url.href)) throw new Error('care-response');
        return {html: await response.text(), url: response.url};
      }
      prefetch(url) {
        if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '') || url.search) return;
        const existing = this.prefetched.get(url.href);
        if (existing && Date.now() - existing.time < 20000) return;
        if (this.prefetched.size >= 4) this.prefetched.delete(this.prefetched.keys().next().value);
        const entry = {time: Date.now()};
        entry.promise = this.fetchPage(url, AbortSignal.timeout(10000)).catch(() => {
          if (this.prefetched.get(url.href) === entry) this.prefetched.delete(url.href);
          return null;
        });
        this.prefetched.set(url.href, entry);
      }
      cancelMotion() { this.presentation.motion.cancel(); }
      reveal() {
        this.cancelMotion();
        this.presentation.motion.reveal([...this.main.querySelectorAll('.ap-care-hero h1,.ap-care-card,.ap-care-faq-card')], {opacity:.25});
      }
      async load(url, {history: fromHistory = false, scroll} = {}) {
        if (this.samePage(url.href, this.url)) { this.request?.abort(); this.request = null; this.busy(false); return; }
        if (!fromHistory) this.remember();
        this.request?.abort();
        const request = this.request = new AbortController();
        const timeout = setTimeout(() => { if (this.request === request) { request.abort(); location.assign(url.href); } }, 12000);
        const navTop = this.main.querySelector('[data-care-navigation]')?.getBoundingClientRect().top || 0;
        const returnToRail = scrollY > 100;
        this.cancelMotion(); this.busy(true, url);
        try {
          const cached = this.prefetched.get(url.href);
          this.prefetched.delete(url.href); // A speculative response is consumed once, including cancelled navigation.
          const response = (cached && Date.now() - cached.time < 20000 && await cached.promise) || await this.fetchPage(url, request.signal);
          const doc = new DOMParser().parseFromString(response.html, 'text/html');
          if (request.signal.aborted || this.request !== request) return;
          const loaded = new Set([...document.querySelectorAll('script[src]')].map(node => node.src));
          const extraScript = [...doc.querySelectorAll('script[src]')].some(node => !loaded.has(new URL(node.getAttribute('src'), response.url).href));
          if (!this.supported(doc) || extraScript || doc.body.dataset.apPage !== document.body.dataset.apPage || doc.documentElement.lang !== document.documentElement.lang || doc.documentElement.dir !== document.documentElement.dir || window.AplovioLayers?.top()) throw new Error('care-integration');
          const styles = [...doc.querySelectorAll('link[rel="stylesheet"]')];
          const currentStyles = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map(node => node.href));
          if (styles.some(node => !currentStyles.has(new URL(node.getAttribute('href'), response.url).href))) throw new Error('care-styles');
          if (!fromHistory) {
            const state = {...history.state};
            delete state.aplovio; delete state.aplovioLayers;
            history.pushState({...state, aplovioCare: {url: url.href, scroll: 0}}, '', url.href);
          }
          this.url = url.href;
          this.main.replaceChildren(...[...doc.querySelector('main').childNodes].map(node => document.importNode(node, true)));
          const meta = 'title,link[rel="canonical"],meta[name="description"],meta[property^="og:"],meta[name^="twitter:"]';
          document.head.querySelectorAll(meta).forEach(node => node.remove());
          doc.head.querySelectorAll(meta).forEach(node => document.head.append(document.importNode(node, true)));
          for (const flag of ['desktop', 'mobile', 'light', 'dark']) document.body.toggleAttribute(`data-ap-initial-header-${flag}`, doc.body.hasAttribute(`data-ap-initial-header-${flag}`));
          this.request = null; this.busy(false);
          const nav = this.main.querySelector('[data-care-navigation]');
          const y = fromHistory ? scroll || 0 : returnToRail ? scrollY + nav.getBoundingClientRect().top - Math.max(0, navTop) : 0;
          scrollTo({top: y, behavior: 'instant'});
          const focus = nav.querySelector('[aria-current="page"]') || this.main;
          focus.focus({preventScroll: true});
          this.reveal(); this.remember();
          document.dispatchEvent(new CustomEvent('aplovio:care-load', {detail: {url: url.href}}));
        } catch (error) {
          if (!request.signal.aborted && this.request === request) location.assign(url.href);
        } finally {
          clearTimeout(timeout);
          if (this.request === request) { this.request = null; this.busy(false); }
        }
      }
    }
    window.AplovioCarePages = new CarePages();
  }
  if (!customElements.get('aplovio-care-contact')) {
    class CareContact extends HTMLElement {
      async connectedCallback() {
        this.abort?.abort();
        const controller = new AbortController();
        this.abort = controller;
        const {signal} = controller;
        await customElements.whenDefined('aplovio-layer');
        if (signal.aborted || !this.isConnected) return;
        const layer = this.querySelector('aplovio-layer');
        const content = this.querySelector('[data-care-form-content]');
        const home = this.querySelector('[data-care-form-home]');
        const body = layer?.querySelector('.ap-layer__body');
        if (!body || !content || !home) return;
        this.layer = layer; this.content = content; this.home = home;
        body.append(content);
        home.hidden = true;
        const triggers = [...this.querySelectorAll('[data-care-form-open]')];
        for (const trigger of triggers) {
          trigger.setAttribute('aria-haspopup','dialog');
          trigger.setAttribute('aria-controls',layer.dialog.id);
          trigger.setAttribute('aria-expanded','false');
        }
        this.addEventListener('click', event => {
          const trigger = event.target.closest('[data-care-form-open], [data-care-chat-open]');
          if (!trigger || !this.contains(trigger) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (trigger.hasAttribute('data-care-chat-open')) this.openChat(trigger, signal);
          else {
            this.content.querySelector('[data-care-chat-unavailable]').hidden = true;
            layer.open(trigger);
          }
        }, {signal});
        for (const state of ['open','close']) layer.addEventListener(`aplovio:layer-${state}`, () => {
          for (const trigger of triggers) trigger.setAttribute('aria-expanded',String(state === 'open'));
        }, {signal});
        const form = content.querySelector('form');
        const pendingKey = 'aplovio:contact:submitted';
        form?.addEventListener('submit', () => {
          // Store only the instance ID. The native form owns all personal data and delivery.
          try { sessionStorage.setItem(pendingKey,form.id); } catch {}
        }, {signal});
        const result = content.querySelector('[data-care-success], [data-care-error]');
        let pending;
        try { pending = sessionStorage.getItem(pendingKey); } catch {}
        if (result && (pending === form?.id || location.hash === `#${form?.id}`)) {
          try { sessionStorage.removeItem(pendingKey); } catch {}
          layer.open(triggers[0]);
          result.focus({preventScroll:true});
        } else if (location.hash === `#${form?.id}`) layer.open(triggers[0]);
        this.addEventListener('shopify:block:select', event => {
          if (event.target.querySelector?.('[data-care-form-open]')) layer.open(triggers[0]);
        }, {signal});
        this.dataset.careReady = 'true';
      }
      async openChat(trigger, signal) {
        if (this.chatPending) return;
        this.chatPending = true;
        const label = trigger.querySelector('[data-care-chat-label]');
        const originalLabel = label.textContent;
        label.textContent = this.dataset.chatOpening;
        trigger.setAttribute('aria-busy', 'true');
        let opened = false;
        try {
          await window.AplovioLazy.asset(this.dataset.chatRuntime, 'script');
          if (!signal.aborted) opened = await window.AplovioChat.open({signal});
        } catch (error) {
          if (!signal.aborted) console.warn('[Aplovio] Live chat is unavailable.', error);
        } finally {
          this.chatPending = false;
          trigger.removeAttribute('aria-busy');
          label.textContent = originalLabel;
        }
        if (!signal.aborted && !opened) {
          this.content.querySelector('[data-care-chat-unavailable]').hidden = false;
          this.layer.open(trigger);
        }
      }
      disconnectedCallback() {
        this.abort?.abort();
        if (this.layer?.dialog) window.AplovioLayers?.dispose(this.layer.dialog);
        if (this.home && this.content) { this.home.append(this.content); this.home.hidden = false; }
      }
    }
    customElements.define('aplovio-care-contact',CareContact);
  }
  if (!customElements.get('aplovio-care-faq')) {
    class CareFAQ extends HTMLElement {
      connectedCallback() {
        this.abort?.abort(); this.abort = new AbortController();
        const options = {signal:this.abort.signal};
        this.addEventListener('click', event => {
          const summary = event.target.closest('summary');
          const details = summary?.parentElement;
          if (!details?.matches('.ap-care-question') || !window.AplovioLayers) return;
          event.preventDefault();
          const expanded = details.dataset.disclosurePhase === 'closing' || (!details.open && details.dataset.disclosurePhase !== 'opening');
          window.AplovioLayers.disclose(details,details.querySelector('[data-care-answer]'),expanded);
        },options);
        this.addEventListener('shopify:block:select', event => {
          const details = event.target.closest?.('.ap-care-question');
          if (details) { window.AplovioLayers?.cancelDisclosure(details); details.open = true; details.querySelector('summary')?.setAttribute('aria-expanded','true'); }
        },options);
      }
      disconnectedCallback() {
        this.abort?.abort();
        for (const details of this.querySelectorAll('details')) window.AplovioLayers?.cancelDisclosure(details);
      }
    }
    customElements.define('aplovio-care-faq',CareFAQ);
  }
})();
